import SharedWorker from '@okikio/sharedworker';
import mitt from 'mitt';
import { createPinia, PiniaVuePlugin } from 'pinia';
import Vue from 'vue';

import { deviceIdPlugin } from '@/plugins/aml-fingerprint';
import vuetify from '@/plugins/vuetify';
import router, { CustomVueRouter } from '@/router';
import { defaultBroadcastServiceReceiver } from '@/utils/broadcast-service';

import ChangeTitlePlugin from './plugins/change-title';
import ErrorTrackingPlugin from './plugins/error-tracking';
import HttpPlugin from './plugins/http';
import NotificationsPlugin from './plugins/notifications';
import SharedFiltersPlugin from './plugins/shared-filters';

import { errortrackingNew } from './api/endpoints/misc';
import { useGlobalAppState } from './store/app-state';

import App from './App.vue';
import SiteFooter from '@/components/common/SiteFooter.vue';
import SiteLogo from '@/components/widgets/SiteLogo.vue';

deviceIdPlugin.init();

const bus = mitt();

const signalr = new SharedWorker(new URL('./signalr.sharedworker', import.meta.url), { type: 'module' });
signalr.start();

const busSignalrEventType = 'signalr:event'; // TODO: в константы?
signalr.addEventListener('message', ({ data: [event, data] }) => {
  // eslint-disable-next-line no-console
  import.meta.env.DEV && console.log(`%c${busSignalrEventType} -> %s`, 'color: black; background-color: orange; padding: 0.25em', event, data);

  bus.emit(busSignalrEventType, { event, data });
});

Vue.use(PiniaVuePlugin);
Vue.use(ChangeTitlePlugin);
Vue.use(NotificationsPlugin, { bus });

const pinia = createPinia();
pinia.use(({ options, store }) => {
  // проверяем есть ли action onSignalrEvent у store. Если есть - отправляем туда signalr события через emitt
  const { onSignalrEvent } = options.actions;
  if (onSignalrEvent) {
    const busHandler = onSignalrEvent.bind(store);
    bus.on(busSignalrEventType, busHandler);

    const originalDispose = store.$dispose.bind(store);
    store.$dispose = () => {
      bus.off(busSignalrEventType, busHandler);
      originalDispose();
    };
  }

  if (typeof options.beforeDispose === 'function') {
    const originalDispose = store.$dispose.bind(store);

    store.$dispose = () => {
      options.beforeDispose.call(store);
      originalDispose();
    };
  }
});

Vue.use(HttpPlugin, {
  afterInstall(instance) {
    instance.interceptors.request.use((request) => {
      const { deviceId, userAgentInterpreted, companyId } = useGlobalAppState();

      if (companyId) {
        request.headers.set('companyid', companyId);
      }

      if (deviceId) {
        request.headers.set('Device-Id', deviceId);
        request.headers.set('Device-Detect', userAgentInterpreted);
      }

      return request;
    });

    instance.interceptors.response.use(
      // onFulfilled
      undefined,
      // onRejected
      (wrapper) => {
        const globalAppState = useGlobalAppState();

        if (wrapper.response?.err === 'unauthorized')
          globalAppState.deactivateSession();

        // fixme: не очень адекватный критерий
        if (wrapper.response?.errdesc?.includes('device_id')) {
          // resolve можно потому, что предусмотрен режим работы приложения и без deviceId
          return Promise.resolve(wrapper);
        }

        if (wrapper.config?.localErrorHandle !== true) {
          if (wrapper.name === 'CanceledError') {
            return Promise.reject(wrapper);
          }

          let message;
          if (wrapper.err) {
            message = wrapper.response.errdesc || wrapper.response.err || 'Сервер вернул неизвестную ошибку';
          }
          else if (wrapper.response) {
            message = `Ошибка ${wrapper.response.status}`;
          }
          else if (wrapper.request) {
            message = 'Не удалось получить ответ от сервера';
          }
          else {
            message = 'Что-то пошло не так';
          }

          Vue.notify({
            text: message,
            type: 'error',
          });
        }

        return Promise.reject(wrapper);
      }
    );
  },
});

if (window) {
  window.BUILD_UTC_AT = __BUILD_UTC_AT__;

  if (import.meta.env.DEV && import.meta.env.VITE_APP_BUILD_COMMIT_SHORT_SHA) {
    window.COMMIT_SHORT_SHA = import.meta.env.VITE_APP_BUILD_COMMIT_SHORT_SHA;
  }
}

Vue.use(ErrorTrackingPlugin, {
  handler: (data) => {
    if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
      let notifyMessage;

      if (data?.error?.vmname) {
        notifyMessage = `${data.error.vmname}: ${data.error.msg}`;
      }
      else if (data?.error?.msg) {
        notifyMessage = data.error.msg;
      }

      Vue.notify({
        text: `(dev mode): ${notifyMessage || 'Ошибка JavaScript Runtime'}`,
        type: 'warning',
        duration: 10,
      });

      if (import.meta.env.DEV) {
        console.warn(errortrackingNew, data);
        return;
      }
    }

    Vue.http.post(errortrackingNew, data, { globalLoader: false });
  },
});
Vue.use(CustomVueRouter);

Vue.use(SharedFiltersPlugin);

defaultBroadcastServiceReceiver?.addEventListener('message', (event) => {
  if (event?.data?.type === 'login') {
    // TODO: в signalr отправлять/принимать userid с которого установилось соединение и отправлять: на основе этого определять - переподключаться или нет
    signalr?.postMessage(['reconnect']);
    // TODO: update pinia store
  }

  const { deactivateSession, isAuthenticated } = useGlobalAppState();
  if (isAuthenticated)
    deactivateSession();
});

new Vue({
  components: {
    SiteLogo,
    SiteFooter,
  },

  provide() {
    return {
      bus,
      notify: Vue.notify,
      http: Vue.http
    };
  },

  router,
  vuetify,
  pinia,
  render: (h) => h(App),
}).$mount('#app');
